import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { slugify } from '@/lib/slug'
import { cn } from '@/lib/utils'
import type { CompanyTag } from './hooks/use-company-tags'

interface TagManagementModalProps {
  tags: CompanyTag[]
  onAdd: (data: {
    name: string
    slug?: string
    description?: string
    order?: number
  }) => Promise<CompanyTag>
  onUpdate: (
    id: string,
    data: {
      name?: string
      slug?: string
      description?: string | null
      order?: number
    },
  ) => Promise<CompanyTag>
  onDelete: (id: string) => Promise<void>
  onReorder: (orderedIds: string[]) => Promise<void>
  onClose: () => void
}

function SortableTagItem({
  tag,
  editingId,
  editTag,
  setEditTag,
  setEditingId,
  deleteConfirmId,
  setDeleteConfirmId,
  isSubmitting,
  onUpdate,
  onDelete,
}: {
  tag: CompanyTag
  editingId: string | null
  editTag: { name: string; slug: string; description: string }
  setEditTag: (v: { name: string; slug: string; description: string }) => void
  setEditingId: (id: string | null) => void
  deleteConfirmId: string | null
  setDeleteConfirmId: (id: string | null) => void
  isSubmitting: boolean
  onUpdate: (id: string) => void
  onDelete: (id: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {editingId === tag.id ? (
        <div className="bg-muted/30 space-y-2 rounded-lg p-3">
          <Input
            type="text"
            value={editTag.name}
            onChange={(e) => setEditTag({ ...editTag, name: e.target.value })}
            placeholder="Tag name"
          />
          <Input
            type="text"
            value={editTag.slug}
            onChange={(e) => setEditTag({ ...editTag, slug: e.target.value })}
            placeholder="Slug (auto-generated)"
          />
          <Input
            type="text"
            value={editTag.description}
            onChange={(e) =>
              setEditTag({
                ...editTag,
                description: e.target.value,
              })
            }
            placeholder="Description (optional)"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditingId(null)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => onUpdate(tag.id)}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      ) : deleteConfirmId === tag.id ? (
        <div className="bg-destructive/5 border-destructive/20 rounded-lg border p-3">
          <p className="text-foreground mb-2 text-sm">
            Delete <strong>{tag.name}</strong>? This will remove the tag from
            all companies.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDeleteConfirmId(null)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(tag.id)}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="hover:bg-muted/50 group flex items-center gap-2 rounded-lg p-3 transition-colors">
          <button
            type="button"
            className="cursor-grab touch-none active:cursor-grabbing"
            aria-label={`Reorder ${tag.name}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={16} className="text-muted-foreground" />
          </button>
          <div className="flex-1">
            <p className="text-foreground text-sm font-medium">{tag.name}</p>
            {tag.description && (
              <p className="text-muted-foreground text-xs">{tag.description}</p>
            )}
          </div>
          <div className={cn(REVEAL_ON_HOVER, 'flex items-center gap-1')}>
            <button
              type="button"
              onClick={() => {
                setEditingId(tag.id)
                setEditTag({
                  name: tag.name,
                  slug: tag.slug,
                  description: tag.description || '',
                })
              }}
              className="text-muted-foreground hover:text-foreground rounded p-1"
              aria-label={`Edit ${tag.name}`}
              title="Edit tag"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirmId(tag.id)}
              className="text-muted-foreground hover:text-destructive rounded p-1"
              aria-label={`Delete ${tag.name}`}
              title="Delete tag"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function TagManagementModal({
  tags,
  onAdd,
  onUpdate,
  onDelete,
  onReorder,
  onClose,
}: TagManagementModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [newTag, setNewTag] = useState({ name: '', slug: '', description: '' })
  const [editTag, setEditTag] = useState({
    name: '',
    slug: '',
    description: '',
  })
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = tags.findIndex((t) => t.id === active.id)
    const newIndex = tags.findIndex((t) => t.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(tags, oldIndex, newIndex)
    onReorder(reordered.map((t) => t.id))
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTag.name.trim()) {
      toast.error('Tag name is required')
      return
    }
    setIsSubmitting(true)
    try {
      await onAdd({
        name: newTag.name.trim(),
        slug: newTag.slug.trim() || undefined,
        description: newTag.description.trim() || undefined,
        order: tags.length,
      })
      toast.success('Tag created')
      setNewTag({ name: '', slug: '', description: '' })
      setShowAddForm(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create tag')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdate = async (id: string) => {
    if (!editTag.name.trim()) {
      toast.error('Tag name is required')
      return
    }
    setIsSubmitting(true)
    try {
      await onUpdate(id, {
        name: editTag.name.trim(),
        slug: editTag.slug.trim() || slugify(editTag.name.trim()),
        description: editTag.description.trim() || null,
      })
      toast.success('Tag updated')
      setEditingId(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update tag')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    setIsSubmitting(true)
    try {
      await onDelete(id)
      toast.success('Tag deleted')
      setDeleteConfirmId(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete tag')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border-border flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg border shadow-lg">
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b p-4">
          <h2 className="text-foreground text-lg font-semibold">
            Manage Company Tags
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {tags.length === 0 && !showAddForm ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-4 text-sm">
                No tags yet. Create your first tag to start categorizing
                companies.
              </p>
              <Button onClick={() => setShowAddForm(true)}>
                <Plus size={16} className="mr-2" />
                Create Tag
              </Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={tags.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {tags.map((tag) => (
                    <SortableTagItem
                      key={tag.id}
                      tag={tag}
                      editingId={editingId}
                      editTag={editTag}
                      setEditTag={setEditTag}
                      setEditingId={setEditingId}
                      deleteConfirmId={deleteConfirmId}
                      setDeleteConfirmId={setDeleteConfirmId}
                      isSubmitting={isSubmitting}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Add form */}
          {showAddForm && (
            <form
              onSubmit={handleAdd}
              className="bg-muted/30 mt-3 space-y-2 rounded-lg p-3"
            >
              <Input
                type="text"
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                placeholder="Tag name (e.g., Big Tech)"
                autoFocus
              />
              <Input
                type="text"
                value={newTag.slug}
                onChange={(e) => setNewTag({ ...newTag, slug: e.target.value })}
                placeholder="Slug (auto-generated from name)"
              />
              <Input
                type="text"
                value={newTag.description}
                onChange={(e) =>
                  setNewTag({ ...newTag, description: e.target.value })
                }
                placeholder="Description (optional)"
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false)
                    setNewTag({ name: '', slug: '', description: '' })
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="border-border flex items-center justify-between border-t p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowAddForm(true)
              setEditingId(null)
              setDeleteConfirmId(null)
            }}
            disabled={showAddForm}
          >
            <Plus size={14} className="mr-1" />
            Add Tag
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}
